/**
 * ExchangeContext — provides the current exchange adapter to all child components.
 * Set via URL param: ?exchange=binance, ?exchange=okx, etc. Defaults to binance.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { ExchangeAdapter, getExchangeAdapter, getAvailableExchanges } from "../exchanges";

interface ExchangeContextType {
  adapter: ExchangeAdapter;
  switchExchange: (name: string) => void;
  availableExchanges: string[];
}

const ExchangeContext = createContext<ExchangeContextType>({
  adapter: getExchangeAdapter("binance"),
  switchExchange: () => {},
  availableExchanges: [],
});

export function ExchangeProvider({ children }: { children: React.ReactNode }) {
  // Derive access level from URL
  const accessLevel = window.location.href.includes("admin") ? 2 : window.location.href.includes("premium") ? 1 : 0;

  // Read initial exchange from URL (block free users from URL-based switching)
  const getInitialExchange = (): string => {
    const params = new URLSearchParams(window.location.search);
    const urlExchange = params.get("exchange");
    return (urlExchange && accessLevel >= 1) ? urlExchange : "binance";
  };

  const [currentExchange, setCurrentExchange] = useState(getInitialExchange);

  // Update favicon & title on mount AND whenever exchange changes (URL param, switch, etc.)
  useEffect(() => {
    const ad = getExchangeAdapter(currentExchange);
    document.title = ad.title;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = ad.favicon;
  }, [currentExchange]);

  const adapter = getExchangeAdapter(currentExchange);

  const switchExchange = useCallback((name: string) => {
    if (accessLevel < 1) return; // Block free users from switching
    setCurrentExchange(name);
    const params = new URLSearchParams(window.location.search);
    params.set("exchange", name);
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  }, [accessLevel]);

  return (
    <ExchangeContext.Provider value={{ adapter, switchExchange, availableExchanges: getAvailableExchanges() }}>
      {children}
    </ExchangeContext.Provider>
  );
}

export function useExchangeAdapter(): ExchangeAdapter {
  return useContext(ExchangeContext).adapter;
}

export function useExchangeSwitch(): (name: string) => void {
  return useContext(ExchangeContext).switchExchange;
}

export function useAvailableExchanges(): string[] {
  return useContext(ExchangeContext).availableExchanges;
}
